import React, { useEffect, useRef, useState } from 'react'
import {
	Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
	DialogContentText, DialogTitle, Paper, Table, TableBody, TableCell,
	TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, useMediaQuery,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { admin } from '../../../api'
import useTwoFactorResetPermission from '../../../hooks/use-two-factor-reset-permission'

export default function TwoFactorManagement() {
	const mobile = useMediaQuery('(max-width:599px)')
	const { allowed, loading: permissionLoading } = useTwoFactorResetPermission()
	const [users, setUsers] = useState([])
	const [loading, setLoading] = useState(false)
	const [search, setSearch] = useState('')
	const [page, setPage] = useState(0)
	const [selected, setSelected] = useState(null)
	const [resetting, setResetting] = useState(false)
	const [notice, setNotice] = useState(null)
	const busy = useRef(false)

	useEffect(() => {
		if (!allowed) return
		let active = true
		setLoading(true)
		admin.getTwoFactorAdmins()
			.then(response => { if (active) setUsers(response.data) })
			.catch(() => { if (active) setNotice({ severity: 'error', text: 'ไม่สามารถโหลดรายชื่อเจ้าหน้าที่ได้ กรุณาลองใหม่' }) })
			.finally(() => { if (active) setLoading(false) })
		return () => { active = false }
	}, [allowed])

	const onReset = async () => {
		if (busy.current || !selected || !allowed) return
		busy.current = true
		setResetting(true)
		setNotice(null)
		try {
			await admin.resetTwoFactor(selected.id)
			setUsers(previous => previous.map(user => user.id === selected.id ? { ...user, isTwoFactorEnabled: false } : user))
			setNotice({ severity: 'success', text: `Reset 2FA ของ ${selected.username} สำเร็จ กรุณาลงทะเบียนแอปใหม่เมื่อเข้าสู่ส่วนที่เปิด 2FA ครั้งถัดไป` })
			setSelected(null)
		} catch (error) {
			setSelected(null)
			setNotice({ severity: 'error', text: error.response?.status === 403 ? 'ไม่มีสิทธิ์ reset 2FA' : 'ไม่สามารถ reset 2FA ได้ กรุณาลองใหม่' })
		} finally {
			busy.current = false
			setResetting(false)
		}
	}

	if (permissionLoading) return <Box p={3}><CircularProgress size={24} /> กำลังตรวจสอบสิทธิ์...</Box>
	if (!allowed) return <Box p={3}><Alert severity="error">ไม่มีสิทธิ์จัดการ 2FA</Alert></Box>

	const filtered = users.filter(user => [user.id, user.username, user.firstname, user.lastname, user.email]
		.some(value => String(value ?? '').toLowerCase().includes(search.toLowerCase().trim())))
	return (
		<Box p={3}>
			<Typography variant="h5" gutterBottom>จัดการ 2FA</Typography>
			<Typography color="textSecondary" paragraph>Reset เมื่อเจ้าหน้าที่เปลี่ยนโทรศัพท์หรือไม่สามารถใช้แอปยืนยันตัวตนเดิมได้ โดยมีผลกับ 2FA ของบัญชีในทุกส่วนของระบบ</Typography>
			{notice && <Box mb={2}><Alert severity={notice.severity}>{notice.text}</Alert></Box>}
			<TextField id="two-factor-search" label="ค้นหา ID, username, ชื่อ หรืออีเมล" variant="outlined" size="small" fullWidth value={search}
				onChange={event => { setSearch(event.target.value); setPage(0) }} />
			<Box mt={2}>
				<Paper>
					{mobile ? (
						<Box p={2}>
							{loading ? <Typography>กำลังโหลด...</Typography> : filtered.slice(page * 10, page * 10 + 10).map(user => (
								<Box key={user.id} mb={2}>
									<Typography variant="subtitle1">{user.username} (ID {user.id})</Typography>
									<Typography>{[user.firstname, user.lastname].filter(Boolean).join(' ')}</Typography>
									<Typography color="textSecondary">{user.isTwoFactorEnabled ? 'ลงทะเบียนแล้ว' : 'ยังไม่ได้ลงทะเบียน'}</Typography>
									<Box mt={1}><Button fullWidth variant="outlined" color="secondary" disabled={resetting} onClick={() => { setNotice(null); setSelected(user) }}>Reset 2FA</Button></Box>
								</Box>
							))}
							{!loading && filtered.length === 0 && <Typography>ไม่พบเจ้าหน้าที่</Typography>}
						</Box>
					) : <TableContainer>
						<Table aria-label="รายชื่อเจ้าหน้าที่สำหรับ reset 2FA">
							<TableHead><TableRow><TableCell>ID</TableCell><TableCell>Username</TableCell><TableCell>ชื่อเจ้าหน้าที่</TableCell><TableCell>สถานะการลงทะเบียน 2FA</TableCell><TableCell>จัดการ</TableCell></TableRow></TableHead>
							<TableBody>
								{loading ? <TableRow><TableCell colSpan={5}>กำลังโหลด...</TableCell></TableRow> : filtered.slice(page * 10, page * 10 + 10).map(user => (
									<TableRow key={user.id}>
										<TableCell>{user.id}</TableCell><TableCell>{user.username}</TableCell>
										<TableCell>{[user.firstname, user.lastname].filter(Boolean).join(' ')}</TableCell>
										<TableCell>{user.isTwoFactorEnabled ? 'ลงทะเบียนแล้ว' : 'ยังไม่ได้ลงทะเบียน'}</TableCell>
										<TableCell><Button variant="outlined" color="secondary" disabled={resetting} onClick={() => { setNotice(null); setSelected(user) }}>Reset 2FA</Button></TableCell>
									</TableRow>
								))}
								{!loading && filtered.length === 0 && <TableRow><TableCell colSpan={5}>ไม่พบเจ้าหน้าที่</TableCell></TableRow>}
							</TableBody>
						</Table>
					</TableContainer>}
					<TablePagination component="div" count={filtered.length} page={page} rowsPerPage={10} rowsPerPageOptions={[10]}
						onPageChange={(_, nextPage) => setPage(nextPage)} labelDisplayedRows={({ from, to, count }) => `${from}–${to} จาก ${count}`} />
				</Paper>
			</Box>
			<Dialog open={Boolean(selected)} onClose={() => { if (!resetting) setSelected(null) }} aria-labelledby="reset-2fa-title">
				<DialogTitle id="reset-2fa-title">ยืนยันการ reset 2FA</DialogTitle>
				<DialogContent><DialogContentText>ต้องการ reset 2FA ของ {selected?.username} (ID {selected?.id}) ใช่หรือไม่? บัญชีนี้ต้องลงทะเบียนแอปใหม่เมื่อเข้าสู่ส่วนที่เปิด 2FA ครั้งถัดไป</DialogContentText></DialogContent>
				<DialogActions><Button disabled={resetting} onClick={() => setSelected(null)}>ยกเลิก</Button><Button color="secondary" disabled={resetting} onClick={onReset}>{resetting ? 'กำลัง reset...' : 'ยืนยัน reset 2FA'}</Button></DialogActions>
			</Dialog>
		</Box>
	)
}
